import React, { useState, useEffect, useRef, useCallback } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { clone } from 'ramda';
import debounce from 'lodash.debounce';
import {
  DataGrid,
  PopupMenu,
  AutoOpenTimePicker,
  DatePicker,
  DialogBox,
  Drawer,
  Button
} from 'quantux-ui';
import {
  BASE_URL,
  DRAWER_BREAK_POINT,
  debugLogToConsole,
  formatPhoneNumber,
  getDateString,
  getShortWeekDay,
  padZero
} from '../../utils';
import AvailDay from './avail-day';
import { blue, gray, salmon } from '../../theme';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;
const DOCS_PER_PAGE = 50;
const START_DATE = new Date();
const DEFAULT_CELL_STYLE = {
  font: '12px Lato',
  // padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};

const DATE_COL = {
  text: 'Date 1',
  field: 'd1',
  key: 'd1',
  dataType: 'string',
  // Appearance
  visible: true,
  pin: 'none',
  isPinnedOnMobile: true,
  // Sorting
  sortable: false,
  sortOrder: 0,
  // Filter
  filter: false,
  isCustomFilter: false,
  showFilterHint: false,
  filterType: 'exactMatch',
  filterMenuType: 'basic', // possible  options => basic, advanced, none
  basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
  advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
  disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
  // Cell click behavior
  enableCellClick: false,
  // Styles
  colStyles: { width: 209, minWidth: 209, maxWidth: 209, textAlign: 'left' },
  colCellsStyles: { textAlign: 'left' }
};
const TODAY = new Date();
const INIT_HEADER_DEFS = [
  // Provider
  {
    text: 'ALL Providers',
    field: 'provider',
    key: 'provider',
    dataType: 'string',
    // Appearance
    visible: true,
    pin: 'left',
    isPinnedOnMobile: true,
    // Sorting
    sortable: true,
    sortOrder: 0,
    // Filter
    filter: false,
    isCustomFilter: false,
    showFilterHint: false,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
    // Cell click behavior
    enableCellClick: false,
    // Styles
    colStyles: { width: 172, minWidth: 172, maxWidth: 172, textAlign: 'left' },
    colCellsStyles: { textAlign: 'left' },
    cellRenderer: (data) => {
      return (
        <div>
          <div>
            {`${data.fullName}, ${data.credentials}`}
          </div>
          <div>
            {formatPhoneNumber(data.mobile)}
          </div>
        </div>
      );
    }
  }
];

const PPAGrid = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [datesToLoad, setDatesToLoad] = useState(31);
  const [headerDefs, setHeaderDefs] = useState(INIT_HEADER_DEFS);
  const [gridData, setGridData] = useState([]);
  const [practiceProviders, setPracticeProviders] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 550);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 210);
  const [isMobileLayout, setMobileLayout] = useState(false);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  /* _________ Menus _________ */
  // --- Availability Menu
  const [availCursorX, setAvailCursorX] = useState(0);
  const [availCursorY, setAvailCursorY] = useState(0);
  const [isAvaiabilityMenuOpen, setIsAvaiabilityMenuOpen] = useState(false);
  // --- Call Menu
  const [callCursorX, setCallCursorX] = useState(0);
  const [callCursorY, setCallCursorY] = useState(0);
  const [isCallMenuOpen, setIsCallMenuOpen] = useState(false);
  // --- Start Time Menu
  const [sTimeCursorX, setSTimeCursorX] = useState(0);
  const [sTimeCursorY, setSTimeCursorY] = useState(0);
  const [isSTimeMenuOpen, setIsSTimeMenuOpen] = useState(false);
  // --- End Time Menu
  const [eTimeCursorX, setETimeCursorX] = useState(0);
  const [eTimeCursorY, setETimeCursorY] = useState(0);
  const [isETimeMenuOpen, setIsETimeMenuOpen] = useState(false);
  // --- Days Menu
  const [isDaysMenuOpen, setIsDaysMenuOpen] = useState(false);
  // --- Start Data
  const [startDateDay, setStartDateDay] = useState(START_DATE.getDate());
  const [startDateMonth, setStartDateMonth] = useState(START_DATE.getMonth());
  const [startDateYear, setStartDateYear] = useState(START_DATE.getFullYear());
  const [isStartDatePristine, setIsStartDatePristine] = useState(false);
  // --- Grid Toolbar Menu
  const [showGridToolbarMenu, setShowGridToolbarMenu] = useState(false);
  // --- Practice Policy Dialog
  const [showPracticePolicyDialog, setShowPracticePolicyDialog] = useState(false);
  // --- Provider Policy Dialog
  const [showProviderPolicyDialog, setShowProviderPolicyDialog] = useState(false);
  // --- Active Data
  const [activeData, setActiveData] = useState({ status: '' });
  // --- Practice Availability Rules
  const [tmpPracticeAvailabilityRules, setTmpPracticeAvailabilityRules] = useState({});
  const [practiceAvailabilityRules, setPracticeAvailabilityRules] = useState({
    0: { status: 'unavailable' }, // Sunday
    1: { status: 'available', startHour: 7, startMinute: 5, endHour: 19, endMinute: 0 }, // Monday
    2: { status: 'available', startHour: 8, startMinute: 10, endHour: 20, endMinute: 1 }, // Tuesday
    3: { status: 'available', startHour: 9, startMinute: 20, endHour: 21, endMinute: 2 }, // Wed
    4: { status: 'available', startHour: 10, startMinute: 30, endHour: 22, endMinute: 3 }, // Thursday
    5: { status: 'available', startHour: 11, startMinute: 40, endHour: 23, endMinute: 4 }, // Friday
    6: { status: 'unavailable' } // Saturday
  });
  // Provider Availability Rules
  const [tmpProviderAvailabilityRules, setTmpProviderAvailabilityRules] = useState({});
  // --- Date Overtide Dialog
  const [showDateOverrideDialog, setShowDateOverrideDialog] = useState(false);

  // == Refs =========================================================
  const gridParentRef = useRef();
  const gridEllipsisMenuRef = useRef();
  const providerAvailRef = useRef();
  const providerCallRef = useRef();
  const startTimeRef = useRef();
  const endTimeRef = useRef();
  const daysRef = useRef();
  const gridToolbarMenuRef = useRef();

  // == Window Event Handlers ========================================
  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : gridParentRefWidth;
      setGridWidth(Math.min(tmp, 1670));
      setGridHeight(window.innerHeight - 210);
      // setGridHeight(window.innerHeight - 600);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  const debouncedHandleWindowResize = useCallback(debounce(handleWindowResize, 500), []);

  // == Network Ops ==================================================
  const getData = () => {
    setShowLoader(true);
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    fetch(`${BASE_URL}/practice/getPracticeProviders`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          setShowLoader(false);
          const data = [];
          result.users.forEach(user => {
            const add1 = user.mailingAddress.streetAddress1;
            const suite = user.mailingAddress.suite;
            const city = user.mailingAddress.city;
            const state = user.mailingAddress.state;
            const zip = user.mailingAddress.zip;
            const accessType = user.accessType;
            if (accessType !== 'Practice Admin' && accessType !== 'Practice Owner' && accessType !== 'Practice Manager') {
              data.push({
                id: user._id || user.id,
                provider: {
                  fullName: `${user.firstName.charAt(0)}.${user.lastName}`,
                  credentials: user.credentials,
                  mobile: user.mobile ? user.mobile.replace(/-/g, '') : ''
                },
                mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
                credentials: user.credentials ? user.credentials : '',
                accessType: accessType
              });
            }
          });

          for (let i = 0; i < data.length; i++) {
            const activeRow = data[i];
            for (let j = 0; j < datesToLoad; j++) {
              const tmpDate = new Date(startDateYear, startDateMonth, startDateDay);
              tmpDate.setDate(tmpDate.getDate() + j);
              const dayIndex = tmpDate.getDay();
              const dateOverrides = result.users[i].dateOverrides;
              const dd = tmpDate.getDate();
              const mm = tmpDate.getMonth() + 1;
              const yyyy = tmpDate.getFullYear();
              let mOverride = false;
              // console.log(dateOverrides);
              // if (i === 0) {
              //   console.log(`${padZero(mm)}/${padZero(dd)}/${yyyy}`);
              // }
              if (dateOverrides && dateOverrides[`${padZero(mm)}/${padZero(dd)}/${yyyy}`]) {
                const {
                  status: s,
                  startHour: sh,
                  startMinute: sm,
                  endHour: eh,
                  endMinute: em
                } = dateOverrides[`${padZero(mm)}/${padZero(dd)}/${yyyy}`];
                mOverride = true;
                console.log(`Override for ${padZero(mm)}/${padZero(dd)}/${yyyy} -- ${sh}${sm} to ${eh}${em}`);
              }
              let finalStatus = result.users[i].overrideAvailabilityRules
                ? result.users[i].availabilityRules[dayIndex].status
                : practiceAvailabilityRules[dayIndex].status;
              let finalSH = result.users[i].overrideAvailabilityRules
                ? result.users[i].availabilityRules[dayIndex].startHour
                : practiceAvailabilityRules[dayIndex].startHour;
              let finalSM = result.users[i].overrideAvailabilityRules
                ? result.users[i].availabilityRules[dayIndex].startMinute
                : practiceAvailabilityRules[dayIndex].startMinute;
              let finalEH = result.users[i].overrideAvailabilityRules
                ? result.users[i].availabilityRules[dayIndex].endHour
                : practiceAvailabilityRules[dayIndex].endHour;
              let finalEM = result.users[i].overrideAvailabilityRules
                ? result.users[i].availabilityRules[dayIndex].endMinute
                : practiceAvailabilityRules[dayIndex].endMinute;

              if (mOverride) {
                const {
                  status: s,
                  startHour: sh,
                  startMinute: sm,
                  endHour: eh,
                  endMinute: em
                } = dateOverrides[`${padZero(mm)}/${padZero(dd)}/${yyyy}`];
                finalStatus = s;
                finalSH = sh;
                finalSM = sm;
                finalEH = eh;
                finalEM = em;
              }

              activeRow[`d${j}`] = {
                weekDay: getShortWeekDay(tmpDate.getDay()),
                status: finalStatus,
                startHour: finalSH || 7,
                startMinute: finalSM || 0,
                endHour: finalEH || 17,
                endMinute: finalEM || 0,
                providerIndex: i,
                date: tmpDate,
                isDateOverride: mOverride
                // startHour: practiceAvailabilityRules[dayIndex].startHour,
                // startMinute: practiceAvailabilityRules[dayIndex].startMinute,
                // endHour: practiceAvailabilityRules[dayIndex].endHour,
                // endMinute: practiceAvailabilityRules[dayIndex].endMinute
              };
            }
          }
          // console.log(data);
          setPracticeProviders(result.users);
          setGridData(data);
          // setPracticeProviders(data);
        }
      });
  };

  const getPracticeRules = () => {
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/getPracticeAvailabilityRules`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          console.log(result.practiceRules);
          setShowLoader(false);
          setPracticeAvailabilityRules(result.practiceRules);
        }
      });
  };

  useEffect(() => {
    loadDates();
    getData();
  }, [props.practice]);

  const loadDates = () => {
    const updatedHeaderDefs = clone(INIT_HEADER_DEFS);
    setShowLoader(true);
    for (let i = 0; i < datesToLoad; i++) {
      const tmpDate = new Date(startDateYear, startDateMonth, startDateDay);
      tmpDate.setDate(tmpDate.getDate() + i);
      const day = getShortWeekDay(tmpDate.getDay());
      // console.log(`Day = ${getShortWeekDay(tmpDate.getDay())} & day index = ${tmpDate.getDay()}`);
      const date = `${padZero(tmpDate.getMonth() + 1)}/${padZero(tmpDate.getDate())}/${tmpDate.getFullYear()}`
      updatedHeaderDefs.push({
        text: `${day} ${date}`,
        field: `d${i}`,
        key: `d${i}`,
        dataType: 'string',
        // Appearance
        visible: true,
        pin: 'none',
        isPinnedOnMobile: true,
        // Sorting
        sortable: false,
        sortOrder: 0,
        // Filter
        filter: false,
        isCustomFilter: false,
        showFilterHint: false,
        filterType: 'exactMatch',
        filterMenuType: 'basic', // possible  options => basic, advanced, none
        basicFilterType: 'contains', // possible values are => startsWith, contains, exactMatch, endsWith
        advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
        disabledFilterTypes: ['contains', 'exactMatch', 'startsWith', 'endsWith', 'advanced'],
        // Cell click behavior
        enableCellClick: false,
        // Styles
        colStyles: { width: 209, minWidth: 209, maxWidth: 209, textAlign: 'left' },
        colCellsStyles: { textAlign: 'left' },
        cellRenderer: (data) => {
          // if (data.weekDay === 'Sun') {
          //   debugLogToConsole(data);
          // }
          if (data) {
            return (
              <div>
                {/* Top Row */}
                <div
                  className='fx fx__v-center pl-2'
                  style={{
                    height: '32px',
                    borderBottom: `0.5px solid ${gray[600]}`,
                    color: data.status.indexOf('unavailable') < 0 ? blue[400] : salmon[500]
                  }}
                  onClick={() => handleOnClickDateOverride(data)}
                >
                  {/* Status */}
                  <div className='cursor__pointer'>
                    {data.status === 'available' ? 'Available' : 'Unavailable'}
                    <i className='ml-1 fa fa-chevron-down' />
                  </div>
                  {/* Start Time */}
                  {data.status === 'available' &&
                    <div className='ml-2 cursor__pointer'>
                      {`${padZero(data.startHour)}:${padZero(data.startMinute)}`}
                    </div>
                  }
                  {/* End Time */}
                  {data.status === 'available' &&
                    <div className='ml-2 cursor__pointer'>
                      {`${padZero(data.endHour)}:${padZero(data.endMinute)}`}
                    </div>
                  }
                </div>
                {/* Bottom Row */}
                <div
                  className='fx fx__v-center pl-2 cursor__pointer'
                  style={{ height: '32px', width: '45px', color: blue[400] }}
                  onClick={(e) => {
                    handleOnClickCallMenu(null, null, e);
                  }}
                >
                  <div style={{ color: gray[200] }}>Call</div>
                  <div className='ml-2'>None</div>
                  <i className='ml-1 fa fa-chevron-down' />
                </div>
              </div>
            );
          }
          return (<div />);
        }
      });
    }

    setShowLoader(false);
    setHeaderDefs(updatedHeaderDefs);
  };

  // == Effects ======================================================
  useEffect(() => {
    // ** Important NOTE **
    // loadDates() function should be run first, because it generates the headerDefs for the grid.
    // Once the headerDefs are there, calling getData() function will fill the data array, and the grid
    // can render the availability schedule
    loadDates();
    getData();
  }, [practiceAvailabilityRules]);

  useEffect(() => {
    window.addEventListener('resize', debouncedHandleWindowResize);
    // ** Important NOTE **
    // First load practice rules and then loadDates and finally get practice provider details
    getPracticeRules();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', debouncedHandleWindowResize);
    };
  }, []);

  useEffect(() => {
    loadDates();
    getData();
  }, [
    datesToLoad,
    startDateDay,
    startDateMonth,
    startDateYear
  ]);

  // == Styles =======================================================
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      width: `${gridWidth}px`,
      overflow: 'hidden'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '40px',
      border: '1px solid black',
      borderBottom: 'none',
      paddingRight: '16px',
      paddingLeft: '8px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '220px',
      fontSize: '14px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      // width: '138px',
      width: '100%',
      paddingLeft: '8px',
      background: '#103A60',
      ':not(.active):hover': {
        background: '#0066CC',
        color: 'white'
      }
    },
    gridEllipsisMenuItemActive: {
      background: gray[700],
      color: 'white'
    },
    dbAvailRulesHeading: {
      fontSize: '14px',
      borderBottom: `1px solid ${blue[900]}`
    },
    dbAvailRulesHeadingTitle: {
      fontWeight: '700'
    },
    dbAvailRulesHeadingText: {
      width: '262px',
      maxWidth: '262px',
      color: 'white',
      marginLeft: '8px'
    },
    dbAvailSectionHeader: {
      fontWeight: '700'
    },
    sideDrawerContainer: {
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerContentArea: {
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0',
      paddingRight: '8px'
    }
  });

  const handleOnClickCell = (col, value, recordId) => {
    console.log(col, value, recordId);
  };

  const handleOnPopupMenu = (e, idx) => {
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickCallMenu = (row, col, e) => {
    const MENU_WIDTH = 150;
    const xVal = window.innerWidth - e.pageX >= MENU_WIDTH ? e.pageX : Math.max(e.pageX - MENU_WIDTH, 0);
    setCallCursorX(xVal);
    setCallCursorY(e.pageY);
    setIsCallMenuOpen(true);
  };

  const handleOnClickSavePracticeRules = () => {
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    formData.append('practiceAvailabilityRules', JSON.stringify(tmpPracticeAvailabilityRules));
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/savePracticeAvailabilityRules`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          setPracticeAvailabilityRules(tmpPracticeAvailabilityRules);
          setShowPracticePolicyDialog(false);
        }
      });
  };

  const updatePracticeRules = (day, data) => {
    const updatedPracticeRules = clone(tmpPracticeAvailabilityRules);
    updatedPracticeRules[day] = data;
    setTmpPracticeAvailabilityRules(updatedPracticeRules);
  };

  const updateProviderRules = (day, data) => {
    const updatedRules = clone(tmpProviderAvailabilityRules);
    updatedRules[day] = data;
    setTmpProviderAvailabilityRules(updatedRules);
  };

  const updatedActiveData = (day, data) => {
    const updatedActiveData = clone(activeData);
    setActiveData({
      ...updatedActiveData,
      ...data
    });
  };

  const handleOnClickSaveProviderRules = () => {
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    formData.append('providerNpi', practiceProviders[popupMenuIndex].npi);
    formData.append('providerAvailabilityRules', JSON.stringify(tmpProviderAvailabilityRules));
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/saveProviderAvailabilityRules`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          // setPracticeAvailabilityRules(tmpPracticeAvailabilityRules);
          setShowProviderPolicyDialog(false);
          loadDates();
          getData();
        }
      });
  };

  const handleOnClickResetProviderAvailabilityRules = () => {
    toggleGridEllipsisMenu(false);
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    formData.append('providerNpi', practiceProviders[popupMenuIndex].npi);
    formData.append('practiceAvailabilityRules', JSON.stringify(practiceAvailabilityRules));
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/resetProviderAvailabilityRules`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          loadDates();
          getData();
        }
      });
  };

  const handleOnClickDateOverride = (data) => {
    console.log(data);
    setActiveData(data);
    setShowDateOverrideDialog(true);
  };

  const handleOnClickAddOverride = () => {
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    formData.append('providerNpi', practiceProviders[activeData.providerIndex].npi);
    const dd = padZero(activeData.date.getDate());
    const mm = padZero(activeData.date.getMonth() + 1);
    const yyyy = activeData.date.getFullYear();
    const tmp = {
      date: `${mm}/${dd}/${yyyy}`,
      status: activeData.status,
      startHour: activeData.startHour,
      startMinute: activeData.startMinute,
      endHour: activeData.endHour,
      endMinute: activeData.endMinute
    };
    formData.append('override', JSON.stringify(tmp));
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/createDateOverride`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          setShowDateOverrideDialog(false);
          loadDates();
          getData();
        }
      });
  };

  const handleOnClickDeleteOverride = () => {
    const formData = new FormData();
    formData.append('practiceNpi', props.practice.npi);
    formData.append('providerNpi', practiceProviders[activeData.providerIndex].npi);
    const dd = padZero(activeData.date.getDate());
    const mm = padZero(activeData.date.getMonth() + 1);
    const yyyy = activeData.date.getFullYear();
    formData.append('date', `${mm}/${dd}/${yyyy}`);
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/practice/deleteDateOverride`, requestOptions)
      .then(res => res.text())
      .then((res) => {
        const result = JSON.parse(res);
        if (result.success) {
          setShowDateOverrideDialog(false);
          loadDates();
          getData();
        }
      });
  };

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      <div className={css(styles.gridContainer)}>
        {/* Grid toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Left Aligned */}
          <div className='fx fx__v-center'>
            <DatePicker
              zIndex={2600}
              className='mb-2'
              left={5}
              date={startDateDay}
              month={startDateMonth}
              year={startDateYear}
              isPrisitine={isStartDatePristine}
              noUnderline
              onChangeDate={(year, month, date) => {
                setStartDateDay(date);
                setStartDateMonth(month);
                setStartDateYear(year);
              }}
            />
            {/* Days Menu */}
            <div
              ref={daysRef}
              style={{
                marginLeft: '16px',
                position: 'relative',
                paddingLeft: '4px',
                zIndex: '2500'
              }}
              onClick={() => setIsDaysMenuOpen(!isDaysMenuOpen)}
            >
              <div
                style={{ color: blue[400] }}
              >
                {`${datesToLoad} Days`}
                <i className='ml-1 fa fa-chevron-down' />
              </div>
              <PopupMenu
                top={0}
                left={0}
                position='absolute'
                zIndex='3000'
                visible={isDaysMenuOpen}
                parent={daysRef}
                onOutsideClick={() => {
                  setIsDaysMenuOpen(false);
                }}
              >
                <div className={css(styles.gridEllipsisMenuContainer)}>
                  {/* 7 Days */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      setDatesToLoad(7);
                      setIsDaysMenuOpen(false);
                    }}
                  >
                    7 Days
                  </div>
                  {/* 15 Days */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      setDatesToLoad(15);
                      setIsDaysMenuOpen(false);
                    }}
                  >
                    15 Days
                  </div>
                  {/* 31 Days */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      setDatesToLoad(31);
                      setIsDaysMenuOpen(false);
                    }}
                  >
                    31 Days
                  </div>
                  {/* 60 Days */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      setDatesToLoad(60);
                      setIsDaysMenuOpen(false);
                    }}
                  >
                    60 Days
                  </div>
                  {/* 180 Days */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      setDatesToLoad(180);
                      setIsDaysMenuOpen(false);
                    }}
                  >
                    180 Days
                  </div>
                </div>
              </PopupMenu>
            </div>
          </div>
          {/* Right Aligned */}
          <div className='fx fx__v-center'>
            {/* Reset Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"><path fill="#96C8FF" fillRule="evenodd" d="M1.926 0h20.145C23.136 0 23.997.861 24 1.926v20.148A1.925 1.925 0 0 1 22.074 24H1.926A1.925 1.925 0 0 1 0 22.074V1.926C0 .861.861 0 1.926 0ZM15 12l4-4 4 4h-3c0 4.42-3.58 8-8 8a7.93 7.93 0 0 1-4.26-1.24L9.2 17.3c.83.45 1.79.7 2.8.7 3.31 0 6-2.69 6-6h-3Zm-3-6c-3.31 0-6 2.69-6 6h3l-4 4-4-4h3c0-4.42 3.58-8 8-8 1.57 0 3.03.46 4.26 1.24L14.8 6.7A5.87 5.87 0 0 0 12 6Z" clipRule="evenodd" /></svg>
            {/* Ellipsis Icon */}
            <div
              ref={gridToolbarMenuRef}
              className='fx fx__v-center'
              style={{ position: 'relative' }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                className='ml-2'
                onClick={() => {
                  setShowGridToolbarMenu(true);
                }}
              >
                <path fill="#96C8FF" fillRule="evenodd" d="M2 0a2 2 0 0 0-2 2v20a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2Zm3 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm12 2a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-5-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" clipRule="evenodd" />
              </svg>
              <PopupMenu
                top={24}
                right={2}
                position='absolute'
                zIndex='3000'
                visible={showGridToolbarMenu}
                parent={gridToolbarMenuRef}
                onOutsideClick={() => {
                  setShowGridToolbarMenu(false);
                }}
              >
                <div
                  className={css(styles.gridEllipsisMenuContainer)}
                  style={{ minWidth: '150px' }}
                >
                  {/* Set Practice Availability Rules */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      setShowGridToolbarMenu(false);
                      setTmpPracticeAvailabilityRules(clone(practiceAvailabilityRules));
                      setShowPracticePolicyDialog(true);
                    }}
                  >
                    Set Practice Rules
                  </div>
                  {/* Reset Practice Rules to Default */}
                  <div
                    className={css(styles.gridEllipsisMenuItem)}
                    onClick={() => {
                      console.log('Reset practice avail rules to default!');
                    }}
                  >
                    Reset Practice Rules
                  </div>
                </div>
              </PopupMenu>
            </div>
          </div>
        </div>

        {/* Grid ellipsis menu */}
        <div
          ref={gridEllipsisMenuRef}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenuRef}
            onOutsideClick={() => { toggleGridEllipsisMenu(false); }}
          >
            <div
              className={css(styles.gridEllipsisMenuContainer)}
              style={{ minWidth: '150px' }}
            >
              {/* Set Provider Rules */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={() => {
                  toggleGridEllipsisMenu(false);
                  console.log(practiceProviders[popupMenuIndex].availabilityRules);
                  setTmpProviderAvailabilityRules(practiceProviders[popupMenuIndex].availabilityRules);
                  setShowProviderPolicyDialog(true);
                }}
              >
                Set Provider Rules
              </div>
              {/* Reset Provider Rules */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickResetProviderAvailabilityRules}
              >
                Reset Provider Rules
              </div>
            </div>
          </PopupMenu>
        </div>

        {/* Availability Popup Menu */}
        <div
          ref={providerAvailRef}
          style={{
            position: 'fixed',
            top: availCursorY,
            left: availCursorX,
            height: '100px',
            width: '130px',
            borderBottom: `0.2px solid ${gray[600]}`,
            paddingLeft: '4px',
            // background: 'yellow', // Use for debugging popup menu outside click event
            zIndex: '2500',
            display: isAvaiabilityMenuOpen ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position='relative'
            zIndex='2501'
            visible={isAvaiabilityMenuOpen}
            parent={providerAvailRef}
            onOutsideClick={() => {
              console.log('Outside click triggerd ....');
              setIsAvaiabilityMenuOpen(false);
            }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Available */}
              <div
                className={`${activeData.status === 'available' ? 'active' : ''} ${css(styles.gridEllipsisMenuItem, activeData.status === 'available' && styles.gridEllipsisMenuItemActive)}`}
                onClick={() => {
                  console.log('Clicked on available');
                }}
              >
                Available
              </div>
              {/* Unavailable */}
              <div
                className={`${activeData.status === 'unavailable' ? 'active' : ''} ${css(styles.gridEllipsisMenuItem, activeData.status === 'unavailable' && styles.gridEllipsisMenuItemActive)}`}
                onClick={() => {
                  console.log('Clicked on unvailable');
                }}
              >
                Unavailable
              </div>
              {/* Unavailable Vacation (paid) */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={() => {
                  console.log('Clicked on Unavailble Vacation (Paid)');
                }}
              >
                Unavailable - Vacation (Paid)
              </div>
              {/* Unavailable Vacation (UnPaid) */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={() => {
                  console.log('Clicked on Unavailble Vacation (UPaid)');
                }}
              >
                Unavailable - Vacation (UnPaid)
              </div>
            </div>
          </PopupMenu>
        </div>

        {/* Call Popup Menu */}
        <div
          ref={providerCallRef}
          style={{
            position: 'fixed',
            top: callCursorY,
            left: callCursorX,
            height: '100px',
            width: '130px',
            borderBottom: `0.2px solid ${gray[600]}`,
            paddingLeft: '4px',
            // background: 'yellow', // Use for debugging popup menu outside click event
            zIndex: '2500',
            display: isCallMenuOpen ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position='relative'
            zIndex='2501'
            visible={isCallMenuOpen}
            parent={providerCallRef}
            onOutsideClick={() => {
              setIsCallMenuOpen(false);
            }}
          >
            <div
              className={css(styles.gridEllipsisMenuContainer)}
              style={{ minWidth: '150px' }}
            >
              {/* None */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={() => {
                  console.log('Clicked on None');
                }}
              >
                None
              </div>
            </div>
          </PopupMenu>
        </div>

        {/* Start Time Menu */}
        <div
          ref={startTimeRef}
          style={{
            position: 'fixed',
            top: sTimeCursorY,
            left: sTimeCursorX,
            height: '100px',
            width: '130px',
            borderBottom: `0.2px solid ${gray[600]}`,
            paddingLeft: '4px',
            // background: 'yellow', // Use for debugging popup menu outside click event
            zIndex: '2500',
            display: isSTimeMenuOpen ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position='relative'
            zIndex='2501'
            visible={isSTimeMenuOpen}
            parent={startTimeRef}
            onOutsideClick={() => {
              setIsSTimeMenuOpen(false);
            }}
          >
            <div
              style={{
                minWidth: '128px',
                background: blue[800]
              }}
            >
              <AutoOpenTimePicker
                hour={activeData.startHour}
                minute={activeData.startMinute}
                noUnderline
                timeFormat='24H'
                timePeriod='AM'
                isPristine={true}
                onChangeTime={(hour, min) => {
                  const provider = practiceProviders[activeData.providerIndex].fullName;
                  console.log(`START TIME: Set date override for ${provider} on date = ${activeData.date} at time ${padZero(hour)}:${padZero(min)}`);
                  setIsSTimeMenuOpen(false);
                }}
                disableTimeFormat
              />
            </div>
          </PopupMenu>
        </div>

        {/* End Time Menu */}
        <div
          ref={endTimeRef}
          style={{
            position: 'fixed',
            top: eTimeCursorY,
            left: eTimeCursorX,
            height: '100px',
            width: '130px',
            borderBottom: `0.2px solid ${gray[600]}`,
            paddingLeft: '4px',
            // background: 'yellow', // Use for debugging popup menu outside click event
            zIndex: '2500',
            display: isETimeMenuOpen ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position='relative'
            zIndex='2501'
            visible={isETimeMenuOpen}
            parent={endTimeRef}
            onOutsideClick={() => {
              setIsETimeMenuOpen(false);
            }}
          >
            <div
              style={{
                minWidth: '128px',
                background: blue[800]
              }}
            >
              <AutoOpenTimePicker
                hour={activeData.endHour}
                minute={activeData.endMinute}
                noUnderline
                timeFormat='24H'
                timePeriod='AM'
                isPristine={true}
                onChangeTime={(hour, min) => {
                  const provider = practiceProviders[activeData.providerIndex].fullName;
                  console.log(`END TIME: Set date override for ${provider} on date = ${activeData.date} at time ${padZero(hour)}:${padZero(min)}`);
                  setIsETimeMenuOpen(false);
                }}
                disableTimeFormat
              />
            </div>
          </PopupMenu>
        </div>

        {/* Practice Policy Dialog */}
        {showPracticePolicyDialog &&
          <Drawer
            placement="right"
            visible={showPracticePolicyDialog}
            toggleDrawer={() => setShowPracticePolicyDialog(false)}
            zIndex="2000"
            width={window.innerWidth <= 700 ? window.innerWidth : 375}
          >
            <div className={css(styles.sideDrawerContainer)}>
              {/* Header */}
              <div className={css(styles.sideDrawerHeader)}>
                <div className='fx fx__hv-center'>
                  <div className='ml-2'>
                    Availability Rules
                  </div>
                </div>
                <div className='fx fx__v-center'>
                  <i
                    className={`ml-2 fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                    onClick={() => setShowPracticePolicyDialog(false)}
                  />
                </div>
              </div>
              {/* Content */}
              <div className={css(styles.sideDrawerContentArea)}>
                <div className={`p-2 fx ${css(styles.dbAvailRulesHeading)}`}>
                  <div className={css(styles.dbAvailRulesHeadingTitle)}>
                    Practice
                  </div>
                  <div className={`text-truncate ${css(styles.dbAvailRulesHeadingText)}`}>
                    {props.practice.practiceName || 'N/A'}
                  </div>
                </div>
                <div className={`p-2 ${css(styles.dbAvailSectionHeader)}`}>
                  Default Availability Rules
                </div>
                <div className='pl-2 pr-2 pb-2' style={{ position: 'relative' }}>
                  <AvailDay
                    day='Monday'
                    dayNumber={1}
                    data={tmpPracticeAvailabilityRules[1]}
                    onUpdate={updatePracticeRules}
                  />
                  <AvailDay
                    day='Tuesday'
                    dayNumber={2}
                    data={tmpPracticeAvailabilityRules[2]}
                    onUpdate={updatePracticeRules}
                  />
                  <AvailDay
                    day='Wednesday'
                    dayNumber={3}
                    data={tmpPracticeAvailabilityRules[3]}
                    onUpdate={updatePracticeRules}
                  />
                  <AvailDay
                    day='Thursday'
                    dayNumber={4}
                    data={tmpPracticeAvailabilityRules[4]}
                    onUpdate={updatePracticeRules}
                  />
                  <AvailDay
                    day='Friday'
                    dayNumber={5}
                    data={tmpPracticeAvailabilityRules[5]}
                    onUpdate={updatePracticeRules}
                  />
                  <AvailDay
                    day='Saturday'
                    dayNumber={6}
                    data={tmpPracticeAvailabilityRules[6]}
                    onUpdate={updatePracticeRules}
                  />
                  <AvailDay
                    day='Sunday'
                    dayNumber={0}
                    data={tmpPracticeAvailabilityRules[0]}
                    onUpdate={updatePracticeRules}
                  />
                </div>
              </div>
              {/* Footer */}
              <div className={css(styles.sideDrawerFooter)}>
                <Button
                  btnType='primary'
                  isOutlineBtn
                  style={{ width: '85px' }}
                  onClick={() => setShowPracticePolicyDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  btnType='primary'
                  onClick={handleOnClickSavePracticeRules}
                  style={{ width: '85px' }}
                  className='ml-2'
                >
                  Apply
                </Button>
              </div>
            </div>
          </Drawer>
        }

        {/* Provider Policy Dialog */}
        {showProviderPolicyDialog &&
          <Drawer
            placement="right"
            visible={showProviderPolicyDialog}
            toggleDrawer={() => setShowProviderPolicyDialog(false)}
            zIndex="2000"
            width={window.innerWidth <= 700 ? window.innerWidth : 375}
          >
            <div className={css(styles.sideDrawerContainer)}>
              {/* Header */}
              <div className={css(styles.sideDrawerHeader)}>
                <div className='fx fx__hv-center'>
                  <div className='ml-2'>
                    Availability Rules (Provider)
                  </div>
                </div>
                <div className='fx fx__v-center'>
                  <i
                    className={`ml-2 fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                    onClick={() => setShowProviderPolicyDialog(false)}
                  />
                </div>
              </div>
              {/* Content */}
              <div className={css(styles.sideDrawerContentArea)}>
                <div className={`p-2 fx ${css(styles.dbAvailRulesHeading)}`}>
                  <div className={css(styles.dbAvailRulesHeadingTitle)}>
                    Practice
                  </div>
                  <div className={`text-truncate ${css(styles.dbAvailRulesHeadingText)}`}>
                    {props.practice.practiceName || 'N/A'}
                  </div>
                </div>
                <div className={`p-2 fx ${css(styles.dbAvailRulesHeading)}`}>
                  <div className={css(styles.dbAvailRulesHeadingTitle)}>
                    Provider
                  </div>
                  <div className={`text-truncate ${css(styles.dbAvailRulesHeadingText)}`}>
                    {`${gridData[popupMenuIndex].provider.fullName}, ${gridData[popupMenuIndex].provider.credentials}`}
                  </div>
                </div>
                <div className={`p-2 ${css(styles.dbAvailSectionHeader)}`}>
                  Default Availability Rules
                </div>
                <div className='pl-2 pr-2 pb-2'>
                  <AvailDay
                    day='Monday'
                    dayNumber={1}
                    data={tmpProviderAvailabilityRules[1]}
                    onUpdate={updateProviderRules}
                  />
                  <AvailDay
                    day='Tuesday'
                    dayNumber={2}
                    data={tmpProviderAvailabilityRules[2]}
                    onUpdate={updateProviderRules}
                  />
                  <AvailDay
                    day='Wednesday'
                    dayNumber={3}
                    data={tmpProviderAvailabilityRules[3]}
                    onUpdate={updateProviderRules}
                  />
                  <AvailDay
                    day='Thursday'
                    dayNumber={4}
                    data={tmpProviderAvailabilityRules[4]}
                    onUpdate={updateProviderRules}
                  />
                  <AvailDay
                    day='Friday'
                    dayNumber={5}
                    data={tmpProviderAvailabilityRules[5]}
                    onUpdate={updateProviderRules}
                  />
                  <AvailDay
                    day='Saturday'
                    dayNumber={6}
                    data={tmpProviderAvailabilityRules[6]}
                    onUpdate={updateProviderRules}
                  />
                  <AvailDay
                    day='Sunday'
                    dayNumber={0}
                    data={tmpProviderAvailabilityRules[0]}
                    onUpdate={updateProviderRules}
                  />
                </div>
              </div>
              {/* Footer */}
              <div className={css(styles.sideDrawerFooter)}>
                <Button
                  btnType='primary'
                  isOutlineBtn
                  style={{ width: '85px' }}
                  onClick={() => setShowProviderPolicyDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  btnType='primary'
                  onClick={handleOnClickSaveProviderRules}
                  style={{ width: '85px' }}
                  className='ml-2'
                >
                  Apply
                </Button>
              </div>
            </div>
          </Drawer>
        }

        {/* Date Override dialog */}
        {showDateOverrideDialog &&
          <DialogBox
            showCloseBtn={true}
            initWidth={DIALOG_WIDTH}
            top={window.innerWidth <= 576 ? 48 : null}
            left={window.innerWidth <= 576 ? 8 : null}
            initHeight={265}
            isOpen={showDateOverrideDialog}
            onClose={() => {
              setShowDateOverrideDialog(false);
            }}
            maskBackground="#004080"
            maskOpacity={0.5}
            closeOnEscKey
            closeOnMaskClick={false}
            header={{
              overrideTheme: true,
              text: 'Provider Schedule Override',
              backColor: '#082840',
              foreColor: gray[200],
              closeColor: '#FF6060',
              fontStyle: 'bold',
              fontSize: '14px'
            }}
          >
            <div>
              <div className={`p-2 fx ${css(styles.dbAvailRulesHeading)}`}>
                <div className={css(styles.dbAvailRulesHeadingTitle)}>
                  Practice
                </div>
                <div className={`text-truncate ${css(styles.dbAvailRulesHeadingText)}`}>
                  {props.practice.practiceName || 'N/A'}
                </div>
              </div>
              <div className={`p-2 fx ${css(styles.dbAvailRulesHeading)}`}>
                <div className={css(styles.dbAvailRulesHeadingTitle)}>
                  Provider
                </div>
                <div className={`text-truncate ${css(styles.dbAvailRulesHeadingText)}`}>
                  {`${gridData[activeData.providerIndex].provider.fullName}, ${gridData[activeData.providerIndex].provider.credentials}`}
                </div>
              </div>
              <div className={`p-2 ${css(styles.dbAvailSectionHeader)}`}>
                {`Manual Date Override for ${activeData.weekDay}, ${getDateString(activeData.date.toString(), false)}`}
              </div>
              <div className='pl-2 pr-2 pb-2'>
                <AvailDay
                  day={activeData.weekDay}
                  dayNumber={activeData.date.getDay()}
                  data={{
                    status: activeData.status,
                    startHour: activeData.startHour,
                    startMinute: activeData.startMinute,
                    endHour: activeData.endHour,
                    endMinute: activeData.endMinute
                  }}
                  onUpdate={updatedActiveData}
                />
              </div>
              {activeData.isDateOverride &&
                <div className='text-right mt-4 pl-2 pr-2 pb-2'>
                  <Button
                    btnType='warning'
                    isOutlineBtn
                    onClick={handleOnClickDeleteOverride}
                    // style={{ width: '85px' }}
                    className='ml-2'
                  >
                    <i className='fa fa-trash mr-2' />
                    Delete Date Override
                  </Button>
                </div>
              }
              {/* Footer */}
              <div
                className='p-2 mt-2'
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  borderTop: `1px solid ${blue[700]}`
                }}
              >
                <Button
                  btnType='primary'
                  isOutlineBtn
                  style={{ width: '85px' }}
                  onClick={() => setShowDateOverrideDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  btnType='primary'
                  onClick={handleOnClickAddOverride}
                  style={{ width: '85px' }}
                  className='ml-2'
                >
                  {activeData.isDateOverride ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </DialogBox>
        }

        {/* DataGrid */}
        <DataGrid
          loader={showLoader}
          icons={icons}
          hideGridHeaderMenus
          headerDefs={headerDefs}
          rowData={gridData}
          filters={filters}
          gridWidth={gridWidth} // was 550
          gridHeight={gridHeight}
          gridHeaderHeight={48}
          gridFooterHeight={32}
          gridContainerHeight='430px'
          rowHeight={64}
          rowsPerPage={DOCS_PER_PAGE}
          defaultCellStyle={DEFAULT_CELL_STYLE}
          showFooter={false}
          showPager={true}
          showEllipsisCol
          enableHover
          totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
          currentPage={currentPage}
          isMobileLayout={isMobileLayout} // window.innerWidth <= 700
          canSelectRows={false}
          onClickCell={handleOnClickCell}
          onPopupMenu={handleOnPopupMenu}
          onFilterTypeUpdate={() => { }}
          onApplyFilter={() => { }}
          onColFilterToggle={() => { }}
          onColPinChange={() => { }}
          onLoadPage={() => { }}
          onSort={() => { }}
          onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
          onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
          onResize={() => { console.warn('Handler not set for onResize prop.'); }}
          onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
          onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
          onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
          onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
        />
      </div>
    </div>
  );
};

export default PPAGrid;
